"use client";

import React from "react";
import Images, { Icons } from "@/app/assets/shared";
import MembershipCard from "./MembershipCard";
import PrimaryBtn from "@/components/PrimaryBtn";
import { useScrollToContact } from "@/lib/ScrollUtils";

interface Membership {
  logo: string;
  subtitle: string;
  title: string;
  logoWidth: number;
  logoHeight: number;
}

export interface MembershipSectionData {
  label?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  buttonText?: string;
  buttonIcon?: string;
  memberships?: Membership[];
}

interface MembershipSectionProps {
  data?: MembershipSectionData;
  showButton?: boolean;
}

export default function MembershipSection({
  data,
  showButton = true,
}: MembershipSectionProps) {
  const handleSmoothScroll = useScrollToContact();

  // Default data — keeps existing usage unchanged
  const defaultMembershipData: Membership[] = [
    {
      logo: Images?.membership.iso1,
      subtitle: "Konstantinfo Has Been Certified",
      title: "ISO/IEC 27001:2013",
      logoWidth: 113,
      logoHeight: 53,
    },
    {
      logo: Images?.membership.iso2,
      subtitle: "Konstant Infosolutions is now",
      title: "ISO 9001:2015 Certified to Meet Latest Quality Goals",
      logoWidth: 79,
      logoHeight: 53,
    },
    {
      logo: Images?.membership.nasscom,
      subtitle: "In 2013, We were given the status of",
      title: "Member of NASSCOM",
      logoWidth: 148,
      logoHeight: 57,
    },
    {
      logo: Images?.membership.igda,
      subtitle: "The year 2012 saw us become",
      title: "Members of the IGDA",
      logoWidth: 238,
      logoHeight: 46,
    },
    {
      logo: Images?.membership.oisv,
      subtitle: "We are also a member of",
      title: "Organization of Independent Software Vendors",
      logoWidth: 107,
      logoHeight: 41,
    },
    {
      logo: Images?.membership.authorize,
      subtitle: "Certified Solutions Provider of",
      title: "Authorize.net",
      logoWidth: 206,
      logoHeight: 55,
    },
    {
      logo: Images?.membership.stpi,
      subtitle: "We are a proud member of STPI",
      title: "Software Technology Park of India",
      logoWidth: 127,
      logoHeight: 50,
    },
    {
      logo: Images?.membership.tie,
      subtitle: "2013 Konstaninfo became member of",
      title: "TIE (The Indus Entrepreneurs)",
      logoWidth: 72,
      logoHeight: 48,
    },
  ];

  const label = data?.label ?? "Membership";

  const title = data?.title ?? (
    <>
      Certified for
      <span className="text-[#225BD4]"> Excellence</span>
    </>
  );

  const description =
    data?.description ??
    "Our certifications represent operational transparency, rigorous evaluation, and commitment to delivering business-centric solutions that support quality outcomes. Through these certifications, we continue to strengthen our foundation of trust, accountability, and professional credibility.";

  const buttonText = data?.buttonText ?? "Let’s Build Something Amazing";

  const buttonIcon = data?.buttonIcon ?? Icons?.chevron;

  const memberships = data?.memberships ?? defaultMembershipData;

  return (
    <section className="w-full bg-white">
      <div
        className="
          max-w-[2500px]
          mx-auto
          w-[calc(100%_-_10%)]
          py-[50px]
          lg:py-[100px]
          grid
          grid-cols-1
          lg:grid-cols-2
          gap-[40px]
          lg:gap-[60px]
          items-start
        "
      >
        {/* LEFT — Content 50% */}
        <div
          className="
    flex flex-col  items-start justify-start gap-[20px] lg:gap-[50px]
    self-start
    lg:sticky
    lg:top-[100px]
  "
        >
          <div className="flex flex-col items-start justify-start gap-[15px]">
            <span className="text-[#00040b] text-[16px] leading-none font-medium">
              {label}
            </span>

            <h2 className="text-[length:var(--section-title)] leading-[120%] text-[#00040b] clash-font font-medium">
              {title}
            </h2>
            <p className="text-[#00040b]/70 text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] leading-[140%]">
              {description}
            </p>
          </div>

          {showButton && (
            <PrimaryBtn
              dark={false}
              text={buttonText}
              iconSrc={buttonIcon}
              onClick={() => {
                handleSmoothScroll();
              }}
            />
          )}
        </div>

        {/* RIGHT — Membership Cards 50% */}
        <div className="grid grid-cols-2 md:grid-cols-4 w-full">
          {memberships.map((item, index) => (
            <MembershipCard
              key={index}
              logo={item.logo}
              subtitle={item.subtitle}
              title={item.title}
              logoWidth={item.logoWidth}
              logoHeight={item.logoHeight}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
